import React, { useState, useEffect, useMemo } from 'react';
import _, { isEmpty, set, get, isArray } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { checkIsNumber, checkCurrency, shortenStringWithTooltip, roundingOff } from '../../../../../Components/helper';
import useShowHideSectionHooks from '../Hooks/useShowHideSectionHooks';
import useValidationHooks from '../Hooks/useValidationHooks';
import useDetailedPageHook from '../Hooks/useDetailedPageHook';
import * as Action from '../../../ProductCosting/store/ProductCosting.action';
import { errorpath, landedInfoFieldPath } from '../../../ProductCosting/fieldPath';

export function LandedInformationTable({ productInfoObj }) {
  const { productCostingstate, setProductCostingState, globalError, grandTotalState, internalState, devOrderId } = productInfoObj;
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const {
    BOMConfigDetails: { productDetails },
  } = useSelector((reduxState) => reduxState);

  const { landedInfo } = globalError;
  const { onChangeCheckIfErrorExist } = useValidationHooks();
  const { calculateProfit, grandTotalLandedInfoCalculation, handleLandedInfoModule } = useDetailedPageHook(productInfoObj);
  const {
    landedInfo: { landedInfoArr, landedInfoColumnArr, currency },
    productInfo: { supplierId },
    productInfo,
    isBreakdown,
  } = productCostingstate;
  const { grandTotal } = grandTotalState;

  const [error, setError] = useState([]);
  const { isDisabled, hideLengthCol } = useShowHideSectionHooks(productInfoObj);

  const checkValidation = () => {
    const required = ['colourway', 'sellingPrice', 'landedCost', 'size'];
    const chRequired = ['colValue'];

    const errors = [];
    let hasErrors = false;
    const data = productCostingstate.landedInfo.landedInfoArr;
    data?.forEach((element, index) => {
      const REQUIRED_FIELD_ERROR = 'This field is required';
      required?.forEach((key) => {
        if (!element[key]) {
          hasErrors = true;
          set(errors, `[${index}][${key}]`, REQUIRED_FIELD_ERROR);
        } else if (typeof element[key] === 'object' && Object.keys(element[key]).length === 0) {
          console.log('from object and value is empty');
          hasErrors = true;
          set(errors, `[${index}][${key}]`, REQUIRED_FIELD_ERROR);
        }
      });
      element.landedInfoRowArr.forEach((childEle, chInde) => {
        chRequired.forEach((key) => {
          if (!childEle[key]) {
            hasErrors = true;
            set(errors, `[${index}][${chInde}][${key}]`, 'This field is required');
          }
        });
      });
    });
    setError(errors);
    return hasErrors;
  };

  const handleInputChange = async (value, path, index) => {
    const copyState = productCostingstate;
    set(copyState, path, value);
    if (path.includes('vat')) {
      copyState?.landedInfo?.landedInfoArr?.forEach((landed, i) => {
        if (landed) {
          const updatedLanded = { ...landed };
          updatedLanded.vat = value;
          copyState.landedInfo.landedInfoArr[i] = updatedLanded;
        }
      });
    }
    if (index >= 0) {
      const profit = await calculateProfit(copyState?.landedInfo?.landedInfoArr);
      set(copyState, landedInfoFieldPath().LANDED_INFO_DATA, profit);
    }

    setProductCostingState((prevState) => ({ ...prevState, ...copyState }));
    if (error && error.length > 0) {
      checkValidation();
    }
  };

  const renderInfoComponent = Array.isArray(landedInfoArr) && landedInfoArr.length > 0;

  const updateError = (path) => onChangeCheckIfErrorExist(path, globalError);
  // // set vvalues when there is chnage in grandTotal
  useEffect(() => {
    const callFn = async (grandTotals, landedInfoArrs) => {
      const copyState = productCostingstate;
      const updatedLandedInfo = await grandTotalLandedInfoCalculation(grandTotals, landedInfoArrs);
      const updateArray = await calculateProfit(updatedLandedInfo);
      set(copyState, landedInfoFieldPath().LANDED_INFO_DATA, updateArray);
      setProductCostingState((prevState) => ({ ...prevState, ...copyState }));
    };
    if (isArray(grandTotal) && isArray(landedInfoArr) && grandTotal?.length > 0 && landedInfoArr?.length > 0) {
      callFn(grandTotal, landedInfoArr);
    }
  }, [grandTotal, landedInfoArr?.length, internalState?.landedExchangeRateInformation?.exchangedRate]);

  useEffect(() => {
    const callFn = async () => {
      const copyState = productCostingstate;
      const res = await dispatch(Action.getProductCostingTemplateDetailsByIds(internalState?.currSelectedTemplateDetails?._id, devOrderId));
      await handleLandedInfoModule(res, copyState);
    };
    if (!_.isEmpty(internalState?.currSelectedTemplateDetails?._id) && devOrderId) callFn();
  }, [isBreakdown, dispatch, devOrderId]);

  const hideShowTopCol = () => {
    const obj = {};
    internalState?.currSelectedTemplateDetails?.topColumnArr?.forEach((topCol) => {
      const keyVal = topCol?.title?.replaceAll(' ', '_').toLowerCase();
      obj[keyVal] = topCol?.isActive;
    });
    return obj;
  };

  const hideShowBotCol = () => {
    const obj = {};
    internalState?.currSelectedTemplateDetails?.bottomColumnArr?.forEach((botCol) => {
      const keyVal = botCol?.title?.replaceAll(' ', '_').toLowerCase();
      obj[keyVal] = botCol?.isActive;
    });
    return obj;
  };

  const topColActiveState = useMemo(() => {
    return hideShowTopCol();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalState?.currSelectedTemplateDetails?.topColumnArr]);

  const botColActiveState = useMemo(() => {
    return hideShowBotCol();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalState?.currSelectedTemplateDetails?.bottomColumnArr]);
  const isSameCurrency = useMemo(() => {
    return productInfo?.currency === currency;
  }, [productInfo?.currency, currency]);

  const renderTableHeaders = internalState?.currSelectedTemplateDetails?.topColumnArr?.map((topCol, i) => {
    if (!topCol?.isActive) {
      return null;
    }

    if (isBreakdown) {
      if (['Length', 'Size'].includes(topCol?.title)) {
        if (topCol?.title === 'Length' && hideLengthCol) {
          return <th key={`length-${topCol?.title}`}>{t('FILTER.LENGTH_RANGE')}</th>;
        }
        if (topCol?.title === 'Size') {
          return <th key={`size-${topCol?.title}`}>{t('FILTER.SIZE_RANGE')}</th>;
        }
      } else if (['Price Per Unit (Converted)', 'Total (Converted)'].includes(topCol?.title)) {
        return !isSameCurrency && topCol?.isActive ? (
          <th key={`converted-${topCol?.title}`} className="w-150px">
            {topCol?.title}
          </th>
        ) : null;
      } else {
        return (
          <th key={`default-${topCol?.title}`} className="w-150px">
            {topCol?.title}
          </th>
        );
      }
    } else {
      if (topCol?.title === 'Length') {
        return hideLengthCol ? (
          <th className="w-150px" key={`length-${topCol?.title}`}>
            {topCol?.title}
          </th>
        ) : null;
      }
      if (['Price Per Unit (Converted)', 'Total (Converted)'].includes(topCol?.title)) {
        return !isSameCurrency && topCol?.isActive ? (
          <th key={`converted-${topCol?.title}`} className="w-150px">
            {topCol?.title}
          </th>
        ) : null;
      }
      return (
        <th key={`default-${topCol?.title}`} className="w-150px">
          {topCol?.title}
        </th>
      );
    }
  });
  return (
    <div id="page">
      <div id="landedInfoCosting" className="table-responsive average-colourway-table table-max-h-50 min-h-200">
        <table id="landedInfoCosting" className="table common-class-bottom common-custom-table layout-fixed  ">
          <thead>
            <tr>
              {isArray(internalState?.currSelectedTemplateDetails?.topColumnArr) && renderTableHeaders}

              {Array.isArray(landedInfoColumnArr) &&
                !isEmpty(landedInfoColumnArr) &&
                landedInfoColumnArr.map((col) => {
                  return col?.isActive ? (
                    <th className="w-120px" key={col?._id}>
                      {col.title} {`${col.type.includes('percentage') ? '(%)' : ''}`}
                    </th>
                  ) : null;
                })}
              {isArray(internalState?.currSelectedTemplateDetails?.bottomColumnArr) &&
                internalState?.currSelectedTemplateDetails?.bottomColumnArr.map((topCol) => {
                  return topCol?.isActive ? <th>{topCol?.title}</th> : null;
                })}
            </tr>
          </thead>
          <tbody>
            {renderInfoComponent &&
              landedInfoArr &&
              landedInfoArr?.length > 0 &&
              landedInfoArr?.map((data, index) => {
                return (
                  <tr key={data?._id}>
                    <td>{data?.colourWay?.name || data?.colourway?.name}</td>
                    <td>
                      {isBreakdown
                        ? Array.isArray(data?.sizeRange) && shortenStringWithTooltip(data?.sizeRange?.map((e) => e).join(' - '), 15)
                        : data?.size || ''}
                    </td>
                    {hideLengthCol && (
                      <td>
                        {isBreakdown
                          ? Array.isArray(data?.lengthRange) && shortenStringWithTooltip(data?.lengthRange?.map((e) => e?.name).join(' - '), 15)
                          : data?.length?.name || ''}
                      </td>
                    )}
                    <td>{data?.pricePerUnitInco || '--'}</td>
                    {topColActiveState['price_per_unit_(converted)'] && !isSameCurrency && <td>{data?.pricePerUnitIncoConverted || '--'}</td>}
                    <td>{data?.totalInco || '--'}</td>
                    {topColActiveState['total_(converted)'] && !isSameCurrency && <td>{data?.totalIncoConverted || '--'}</td>}
                    {topColActiveState?.brand_fee && (
                      <td>
                        {data?.brandFee
                          ? `${data.brandFee} (${
                              (Array.isArray(productDetails?.brand) && productDetails?.brand.length > 0 && productDetails.brand[0]?.royaltyFee) || 0
                            }%)`
                          : '--'}
                      </td>
                    )}
                    {topColActiveState.supplier_fee && (
                      <td>{data?.supplierFee ? `${roundingOff(data.supplierFee, 4, true)} (${supplierId?.companies?.agentFee || 0}%)` : '--'}</td>
                    )}
                    {renderInfoComponent &&
                      !isEmpty(data.landedInfoRowArr) &&
                      Array.isArray(data.landedInfoRowArr) &&
                      data.landedInfoRowArr.map((info, chIndex) => {
                        return (
                          info?.isActive && (
                            <td key={info?._id}>
                              {isDisabled || info?.type === 'source' ? (
                                <>
                                  {roundingOff(info?.colValue, 4, true)}
                                  {info?.subLocation?.type === 'Number_percentage' && '%'}
                                </>
                              ) : (
                                <div className="d-flex align-items-center">
                                  <div className="flex-fill">
                                    <input
                                      value={info.colValue}
                                      className="form-control text-bordered text-left"
                                      onChange={(e) => {
                                        if (checkIsNumber(e.target.value)) {
                                          handleInputChange(e.target.value, landedInfoFieldPath(index, chIndex).LANDED_DYNAMIC_COL_VALUE, index);
                                          if (get(landedInfo, `[${index}][${chIndex}].colValue`, '')) {
                                            updateError(errorpath(index, chIndex).LANDED.ERROR_COL_VALUE);
                                          }
                                        }
                                      }}
                                    />
                                  </div>
                                  <span className="text-nowrap ml-1">{info.type === 'percentage' ? '%' : checkCurrency(currency, undefined, true)}</span>
                                </div>
                              )}
                              <span className="error-message">
                                {get(error, `[${index}][${chIndex}].colValue`, '') || get(landedInfo, `[${index}][${chIndex}].colValue`, '')}
                              </span>
                            </td>
                          )
                        );
                      })}
                    <td>{data?.pricePerUnitLanded || '0'}</td>
                    <td>{data?.totalLanded || '0'}</td>
                    {botColActiveState['selling_price(gross)'] && (
                      <td>
                        {isDisabled ? (
                          data.sellingPrice
                        ) : (
                          <>
                            <div className="d-flex align-items-center">
                              <div className="flex-fill">
                                <input
                                  value={data.sellingPrice}
                                  className="form-control text-bordered text-left"
                                  onChange={(e) => {
                                    if (checkIsNumber(e.target.value)) {
                                      handleInputChange(e.target.value, landedInfoFieldPath(index).SELLING_PRICE, index);
                                    }
                                    if (get(landedInfo, `[${index}].sellingPrice`, '')) {
                                      updateError(errorpath(index).LANDED.ERROR_SELLINGPRICE);
                                    }
                                  }}
                                  type="text"
                                />
                              </div>
                              <span className="text-nowrap ml-1">{checkCurrency(currency, undefined, true)}</span>
                            </div>
                            <span className="error-message">
                              {' '}
                              {get(error, `[${index}].sellingPrice`, '') || get(landedInfo, `[${index}].sellingPrice`, '')}
                            </span>
                          </>
                        )}
                      </td>
                    )}
                    {botColActiveState?.vat && !isDisabled && (
                      <td>
                        {index === 0 ? (
                          <>
                            <div className="d-flex">
                              <input
                                type="text"
                                className="form-control text-bordered"
                                value={data?.vat}
                                onChange={(e) => {
                                  if (checkIsNumber(e.target.value)) {
                                    handleInputChange(e.target.value, landedInfoFieldPath(index).VAT, index);
                                  }
                                }}
                              />
                            </div>
                            <span className="error-message" />
                          </>
                        ) : (
                          data?.vat
                        )}
                      </td>
                    )}
                    {botColActiveState['selling_price(net)'] && <td>{data?.netSalesValue || 0}</td>}
                    <td>
                      {Number.isNaN(data?.profit) ? 0 : data?.profit} {checkCurrency(currency, data?.profit)}
                    </td>
                    {botColActiveState.markup_per_unit && (
                      <td>
                        <span className="f-16 text-medium">{Number.isNaN(data?.markup) ? 0 : data?.markup}</span>
                      </td>
                    )}
                    {(botColActiveState['markup_%_per_unit'] || botColActiveState['margin_%_per_unit']) && (
                      <td>
                        <span className="f-16 text-medium">{Number.isNaN(data?.marginPercent) ? 0 : data?.marginPercent} %</span>
                      </td>
                    )}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default LandedInformationTable;
